import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import searchIcon from "../../pics/SearchIconButton.svg";
import { setCreatorData } from "../../State/userProfileSlice";
import API from "../../api/api";
import VerifiedBadge from "../../pics/VB.png";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import apisecured from "../../api/apisecured";
import DefaultBanner from "../../pics/defaultbanner.png";

function FeaturedCreators() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedTag = searchParams.get("tag");
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [sortedCreators, setSortedCreators] = useState([]);
  const uid = process.env.REACT_APP_DEFAULT_USER_ID;

  // Fetch all creators from API
  const { data: allCreators, isLoading } = useQuery(
    ["all_creators", selectedTag, uid], // Add selectedTag as dependency
    async () => {
      // If a tag is selected, fetch all creators including unverified
      if (selectedTag) {
        const { data } = await apisecured.get(
          `account/get_all_creators_for_tags?uid=${uid}`
        );
        return data;
      } else {
        // Else, fetch only verified creators
        const { data } = await apisecured.get(
          `account/get_all_creators?uid=${uid}`
        );
        return data;
      }
    },
    {
      onSuccess: (data) => {
        const sortedData = [...data].sort((a, b) => {
          const userNameA = (a.user_name || "").toLowerCase();
          const userNameB = (b.user_name || "").toLowerCase();
          return userNameA.localeCompare(userNameB);
        });

        // ✅ If tag exists, filter creators with that tag
        if (selectedTag) {
          const filtered = sortedData.filter((creator) =>
            creator.tagNames?.includes(selectedTag)
          );
          setSortedCreators(filtered);
        } else {
          setSortedCreators(sortedData);
        }
      },
    }
  );

  // Fetch creators for the search bar (separate endpoint)
  useEffect(() => {
    const fetchSearchCreators = async () => {
      try {
        const { data } = await axios.get(
          `${API}account/get_all_creators_for_search?uid=${uid}`
        );
        setSearchResults(data); // Only used for the dropdown
      } catch (error) {
        console.error("Error fetching search creators:", error);
      }
    };

    fetchSearchCreators();
  }, [uid]);

  // Filter search bar results based on searchTerm
  const filteredSearchResults = searchResults.filter((creator) => {
    const displayName = creator.display_name?.toLowerCase() || "";
    const userName = creator.user_name?.toLowerCase() || "";
    return (
      displayName.includes(searchTerm.toLowerCase()) ||
      userName.includes(searchTerm.toLowerCase())
    );
  });
  const handleClick = (creator) => {
    dispatch(
      setCreatorData({
        profileImage: creator.image,
        coverImage: creator.cover_image || DefaultBanner,
        displayName: creator.display_name,
        userName: creator.user_name,
        bio: creator.bio,
        ytURL: creator.yt_url,
        twitterURL: creator.twitter_url,
        instaURL: creator.insta_url,
        fbURL: creator.fb_url,
        accountType: creator.account_type,
        currency: creator.currency,
        isverified: creator.isverified,
      })
    );
  };

  return (
    <div className="grid grid-cols-1 max-w-[850px] mx-auto px-4 gap-6 pb-10 mt-5">
      <h2 className="text-white mt-3  text-[32px] font-[900] leading-[40px] tracking-normal text-left">
        Multiple Tip Goals,{" "}
        <span className=" text-[#00FF8F]  "> All at Once</span>
      </h2>
      <div className="relative w-full py-1">
        <input
          type="text"
          placeholder="Find a Creator to support!"
          className="bg-[#35334D] rounded-full w-full h-[50px] pl-5 pr-[8rem] text-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="absolute right-[3px] top-[6.5px] h-11 flex items-center gap-2 text-black bg-[#00FF8F] rounded-full py-2 px-5">
          <img src={searchIcon} alt="Search" />
          Search
        </button>
        {/* Dropdown appears only when user types */}
        {searchTerm && filteredSearchResults.length > 0 && (
          <div className="absolute w-full bg-[#35334D] mt-1 z-10 rounded-lg">
            {filteredSearchResults.slice(0, 5).map(
              (
                creator // Limit to 5 results
              ) => (
                <div
                  key={creator.uid}
                  className="flex items-center p-2 border-b border-gray-800 last:border-b-transparent"
                  onClick={() => handleClick(creator)} // This should trigger handleClick
                >
                  <Link
                    to={`/${creator.user_name}/current-goals`}
                    className="w-full"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <img
                      src={creator.image}
                      alt={creator.display_name || "No display name"}
                      className="h-10 w-10 rounded-full"
                    />
                    <span className="ml-3 text-white">
                      {creator.display_name || "No display name"}
                    </span>
                    {creator.isverified && (
                      <div style={{ paddingLeft: "5px" }}>
                        <img
                          src={VerifiedBadge}
                          alt=""
                          style={{ width: "20px", height: "20px" }}
                        />
                      </div>
                    )}
                  </Link>
                </div>
              )
            )}
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-x-6 gap-y-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="bg-[#13122E] w-[260px] rounded-[20px] overflow-hidden"
            >
              {/* Cover Image Skeleton */}
              <div className="w-full h-[120px] bg-[#1E1D3D]">
                <Skeleton
                  height={120}
                  baseColor="#35334D"
                  highlightColor="#3D3C57"
                />
              </div>

              {/* Profile Image and Details Skeleton */}
              <div className="p-4">
                {/* Profile Image */}
                <div className="flex justify-center mb-4">
                  <Skeleton
                    circle={true}
                    height={60}
                    width={60}
                    baseColor="#35334D"
                    highlightColor="#3D3C57"
                  />
                </div>

                {/* White Lines (Text Skeletons) */}
                <div className="flex flex-col gap-2">
                  {/* Display Name */}
                  <Skeleton
                    height={10}
                    width="80%"
                    baseColor="#2C2A45"
                    highlightColor="#FFFFFF"
                    className="rounded-md"
                  />
                  {/* Username */}
                  <Skeleton
                    height={10}
                    width="60%"
                    baseColor="#2C2A45"
                    highlightColor="#FFFFFF"
                    className="rounded-md"
                  />
                  {/* Bio Lines */}
                  <Skeleton
                    height={10}
                    width="100%"
                    baseColor="#2C2A45"
                    highlightColor="#FFFFFF"
                    className="rounded-md"
                  />
                  <Skeleton
                    height={10}
                    width="90%"
                    baseColor="#2C2A45"
                    highlightColor="#FFFFFF"
                    className="rounded-md"
                  />
                  <Skeleton
                    height={10}
                    width="70%"
                    baseColor="#2C2A45"
                    highlightColor="#FFFFFF"
                    className="rounded-md"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-x-6 gap-y-6">
          {sortedCreators.map((creator) => (
            <CreatorCard
              key={creator.uid}
              creator={creator}
              onClick={handleClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default FeaturedCreators;

const CreatorCard = ({ creator, onClick }) => {
  return (
    <div
      className="bg-[#13122E] md:max-w-[260px] w-full mx-auto h-full rounded-[20px] overflow-hidden"
      onClick={() => onClick(creator)}
    >
      <Link
        to={`/${creator.user_name}/current-goals`}
        className="bg-[black] py-4 w-full"
      >
        <div className="w-full h-[120px]">
          <img
            src={creator.cover_image || DefaultBanner}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative">
          <div className="absolute left-4 top-[-55px] w-[61px] h-[61px] rounded-full">
            <img
              src={creator.image || "path/to/default/profile/image"}
              alt=""
              style={{
                maxHeight: "55px",
                width: "55px",
                borderRadius: "9999px",
              }}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col gap-2 items-start px-5 py-4 mt-6">
            <div style={{ display: "flex", alignItems: "center" }}>
              <p className="CustomExploreCardHead">{creator.display_name}</p>
              {creator.isverified && (
                <div style={{ paddingLeft: "5px" }}>
                  <img
                    src={VerifiedBadge}
                    alt=""
                    style={{ width: "20px", height: "20px" }}
                  ></img>
                </div>
              )}
            </div>
            <p className="CustomExploreCardSubHead">@{creator.user_name}</p>
            <p className="CustomExploreCardPara whitespace-pre-wrap text-wrap break-words  w-full  mt-1 [text-transform:unset] ">
              {creator.bio ? (
                <>{creator.bio.split("").splice(0, 220).join("")}...</>
              ) : (
                "No biography provided."
              )}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
};
